import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { createEmailChangeRequest } from '@features/workspace/store/actions/create';
import { setNotification } from '@store/features/notification';
import { useAppDispatch } from '@store/hooks';

const contactSchema = yup.object().shape({
  email: yup.string().email().required().max(256).label('New email address'),
});

const EmailChangeDialog: FC<any> = ({
  open,
  onClose,
  user,
  setPendingEmail,
}) => {
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { email: '' },
    resolver: yupResolver(contactSchema),
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      reset();
      setIsLoading(false);
    }
  }, [open]);

  const onSave = async (data: { email: string }) => {
    try {
      setIsLoading(true);
      await dispatch(
        createEmailChangeRequest({
          email: data.email,
          userId: user.id,
        })
      ).unwrap();
      onClose();
      setPendingEmail(data.email);
      dispatch(
        setNotification({
          message: 'A confirmation email has been sent to the user',
          variant: 'success',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle component="div">
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Box display="flex">
            <Typography variant="h5">Change email address</Typography>
          </Box>
          <Box display="flex">
            <IconButton onClick={onClose}>
              <i className="ri-close-fill" style={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          sx={{
            fontWeight: '500',
            fontSize: '16px',
            lineHeight: '20px',
          }}
        >
          <Box>
            <TextField
              fullWidth
              required
              label="New email address"
              placeholder="e.g. joe.soap@example.com"
              variant="outlined"
              {...register('email')}
              error={!!errors.email}
              helperText={errors?.email?.message}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          m: '20px',
          justifyContent: 'space-between',
        }}
      >
        <Button disabled={isLoading} size="large" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={isLoading}
          size="large"
          variant="contained"
          onClick={handleSubmit(onSave)}
        >
          {!isLoading ? 'Change' : 'Changing...'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailChangeDialog;
